/**
 * ContactNotesAlertBanner
 * -----------------------------------------------------------------------------
 * Tarja de destaque exibida no topo da área de chat (logo abaixo do header)
 * quando o contato da conversa aberta possui anotações NÃO LIDAS pelo
 * usuário atual.
 *
 * - Reaproveita a RPC `get_contact_notes_count` e a mesma queryKey
 *   `['contact-notes-count', contactId, tenantId]` já usada pelo FAB
 *   (`ContactNotesButton`). O React Query deduplica a query, então não há
 *   chamada extra de RPC quando os dois componentes coexistem.
 * - Subscreve `postgres_changes` em `public.contact_notes` filtrado por
 *   `contact_id` para invalidar o cache em tempo real → a tarja aparece
 *   automaticamente se outro usuário criar uma anotação enquanto o chat
 *   estiver aberto.
 * - Possui botão de fechar (X) que esconde a tarja apenas na sessão atual;
 *   se uma nova anotação chegar (count aumenta), a tarja volta a aparecer.
 * - Clique abre o modal de listagem `ConversationContactNotesModal` já
 *   existente — sem alterar fluxo de leitura/marcação como lida.
 *
 * @version 1.0.0
 * RELEASE: 2026-06-12 v1.0.0 - Criação inicial.
 */
import { useEffect, useId, useRef, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { ClipboardEdit, X } from 'lucide-react';

import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { cn } from '@/lib/utils';

import { ConversationContactNotesModal } from './ConversationContactNotesModal';

interface ContactNotesAlertBannerProps {
  /** ID do contato da conversa aberta. Sem ele, o componente não renderiza. */
  contactId?: string;
}

export function ContactNotesAlertBanner({ contactId }: ContactNotesAlertBannerProps) {
  const qc = useQueryClient();
  const { currentTenant } = useAuth();

  // ID único por instância — evita colisão com o canal do FAB.
  const instanceId = useId();

  const [openList, setOpenList] = useState(false);
  // Controle de "dispensado" apenas na sessão atual.
  const [dismissed, setDismissed] = useState(false);
  // Guarda o último count visto, para reexibir caso chegue nova anotação.
  const lastSeenCountRef = useRef<number>(0);

  // Reseta o estado quando troca de contato.
  useEffect(() => {
    setDismissed(false);
    lastSeenCountRef.current = 0;
  }, [contactId]);

  // Reaproveita a MESMA queryKey do ContactNotesButton (cache compartilhado).
  const { data: total = 0 } = useQuery({
    queryKey: ['contact-notes-count', contactId, currentTenant?.id],
    enabled: !!contactId && !!currentTenant?.id,
    staleTime: 15_000,
    queryFn: async () => {
      const { data, error } = await supabase.rpc('get_contact_notes_count', {
        p_contact_id: contactId,
        p_tenant_hint: currentTenant?.id ?? null,
      } as never);
      if (error) throw error;
      return Number(data ?? 0);
    },
  });

  // Realtime: invalida a queryKey quando há mudanças em contact_notes.
  useEffect(() => {
    if (!contactId) return;
    const channel = supabase
      .channel(`contact-notes-banner-${contactId}-${instanceId}`)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'contact_notes',
          filter: `contact_id=eq.${contactId}`,
        },
        () => {
          qc.invalidateQueries({ queryKey: ['contact-notes-count', contactId] });
        },
      )
      .subscribe();
    return () => {
      supabase.removeChannel(channel);
    };
  }, [contactId, qc, instanceId]);

  // Se o count aumentou desde a última visualização, força reexibição
  // (mesmo que o usuário tenha dispensado anteriormente).
  useEffect(() => {
    if (total > lastSeenCountRef.current) {
      setDismissed(false);
    }
    lastSeenCountRef.current = total;
  }, [total]);

  // Sem contato ou sem anotações não lidas → não renderiza nada.
  if (!contactId || total <= 0 || dismissed) return null;

  const label =
    total === 1
      ? 'Este contato possui 1 anotação não lida — clique para visualizar'
      : `Este contato possui ${total} anotações não lidas — clique para visualizar`;

  return (
    <>
      <div
        role="button"
        tabIndex={0}
        aria-label={label}
        onClick={() => setOpenList(true)}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            setOpenList(true);
          }
        }}
        className={cn(
          'flex items-center gap-2 px-3 md:px-4 py-2',
          'border-b border-destructive/30 bg-destructive/10 text-destructive',
          'cursor-pointer transition-colors hover:bg-destructive/15',
          'animate-in fade-in slide-in-from-top-1',
        )}
      >
        <ClipboardEdit className="h-4 w-4 shrink-0" aria-hidden />
        <span className="inline-flex h-5 min-w-[20px] items-center justify-center rounded-full bg-destructive px-1.5 text-[10px] font-semibold leading-none text-destructive-foreground">
          {total > 99 ? '99+' : total}
        </span>
        <span className="flex-1 truncate text-xs md:text-sm font-medium">
          {label}
        </span>
        <button
          type="button"
          aria-label="Dispensar aviso"
          onClick={(e) => {
            e.stopPropagation();
            setDismissed(true);
          }}
          className="shrink-0 rounded p-0.5 text-destructive/80 hover:bg-destructive/15 hover:text-destructive"
        >
          <X className="h-3.5 w-3.5" />
        </button>
      </div>

      {openList && (
        <ConversationContactNotesModal
          open={openList}
          onOpenChange={setOpenList}
          contactId={contactId}
        />
      )}
    </>
  );
}
